import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ViewHeader, PortalEmptyState } from "./PortalShared";
import { Plus, Trash2, Send, Receipt, CheckCircle, RefreshCw, DollarSign } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_CFG = {
  draft:   { label: "Draft",   color: "text-white/40 bg-white/[0.04] border-white/[0.08]" },
  sent:    { label: "Sent",    color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  paid:    { label: "Paid",    color: "text-[#00FF88] bg-emerald-500/10 border-emerald-500/20" },
  overdue: { label: "Overdue", color: "text-[#FF9F0A] bg-orange-500/10 border-orange-500/20" },
};

function StatusBadge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.draft;
  return <span className={`px-2 py-0.5 text-[10px] font-bold border rounded-full ${c.color}`}>{c.label}</span>;
}

export default function InvoiceManager({ invoices: initialInvoices = [], onCreated }) {
  const [invoices, setInvoices] = useState(initialInvoices);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ client_name: "", client_email: "", property_address: "", job_description: "", due_date: "", notes: "" });
  const [lineItems, setLineItems] = useState([{ description: "", qty: 1, unit_price: 0, total: 0 }]);

  const updateItem = (i, field, val) => {
    setLineItems(prev => { const u = [...prev]; u[i] = { ...u[i], [field]: val }; u[i].total = Number(u[i].qty) * Number(u[i].unit_price); return u; });
  };
  const grandTotal = lineItems.reduce((s, i) => s + Number(i.qty) * Number(i.unit_price), 0);

  const handleSubmit = async () => {
    if (!form.client_name) { toast.error("Client name required"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/portal-tools/invoices`, {
        ...form, line_items: lineItems.map(i => ({ ...i, total: Number(i.qty) * Number(i.unit_price) }))
      }, { withCredentials: true });
      setInvoices(prev => [res.data, ...prev]);
      toast.success(`Invoice ${res.data.invoice_number} created`);
      setShowForm(false);
      setForm({ client_name: "", client_email: "", property_address: "", job_description: "", due_date: "", notes: "" });
      setLineItems([{ description: "", qty: 1, unit_price: 0, total: 0 }]);
      if (onCreated) onCreated(res.data);
    } catch { toast.error("Failed to create invoice"); }
    finally { setSaving(false); }
  };

  const updateStatus = async (invoiceId, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/portal-tools/invoices/${invoiceId}/status`, { status }, { withCredentials: true });
      setInvoices(prev => prev.map(inv => inv.invoice_id === invoiceId ? { ...inv, status } : inv));
      toast.success(`Invoice marked as ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const totalPaid = invoices.filter(i => i.status === "paid").reduce((s, i) => s + i.total, 0);
  const totalPending = invoices.filter(i => i.status === "sent").reduce((s, i) => s + i.total, 0);

  if (showForm) return (
    <div>
      <ViewHeader title="New Invoice" subtitle="Generate a professional invoice for your work" action="Back" onAction={() => setShowForm(false)} />
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-4">
          <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Invoice Details</p>
          {[["client_name","Client / Company Name"],["client_email","Client Email","email"],["property_address","Property Address"],["job_description","Job Description"],["due_date","Due Date","date"]].map(([k,l,t="text"]) => (
            <div key={k}>
              <label className="text-[10px] text-white/40 block mb-1">{l}</label>
              <Input type={t} value={form[k]} onChange={e => setForm(f => ({...f,[k]:e.target.value}))} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
            </div>
          ))}
        </div>
        <div className="space-y-4">
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Line Items</p>
              <button onClick={() => setLineItems(p => [...p, {description:"",qty:1,unit_price:0,total:0}])} className="text-xs text-[#D4AF37] flex items-center gap-1"><Plus className="w-3 h-3" />Add</button>
            </div>
            {lineItems.map((item, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 items-center">
                <Input value={item.description} onChange={e => updateItem(i,"description",e.target.value)} className="col-span-5 bg-white/[0.02] border-white/[0.06] text-white h-7 text-xs" placeholder="Description" />
                <Input type="number" value={item.qty} onChange={e => updateItem(i,"qty",e.target.value)} className="col-span-2 bg-white/[0.02] border-white/[0.06] text-white h-7 text-xs text-center" />
                <Input type="number" value={item.unit_price} onChange={e => updateItem(i,"unit_price",e.target.value)} className="col-span-3 bg-white/[0.02] border-white/[0.06] text-white h-7 text-xs text-right" placeholder="$" />
                <div className="col-span-1 text-right text-xs text-white/60">${(item.qty * item.unit_price).toFixed(0)}</div>
                <button onClick={() => setLineItems(p => p.filter((_,j)=>j!==i))} className="col-span-1 text-white/20 hover:text-[#FF3B30]"><Trash2 className="w-3 h-3"/></button>
              </div>
            ))}
            <div className="flex justify-between items-center pt-3 border-t border-white/[0.06]">
              <span className="text-white/40 text-sm">Invoice Total</span>
              <span className="text-[#D4AF37] font-bold text-xl">${grandTotal.toLocaleString('en-US',{minimumFractionDigits:2})}</span>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={saving} className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />} Create Invoice
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <ViewHeader title="Invoice Manager" subtitle="Track outstanding and completed invoices" action="New Invoice" onAction={() => setShowForm(true)} actionTestId="create-invoice-btn" />
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[{label:"Total Paid",val:`$${totalPaid.toLocaleString()}`,color:"text-[#00FF88]"},{label:"Pending",val:`$${totalPending.toLocaleString()}`,color:"text-[#D4AF37]"},{label:"Total Invoices",val:invoices.length,color:"text-white"}].map(({label,val,color}) => (
          <div key={label} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4">
            <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>{val}</p>
          </div>
        ))}
      </div>
      {invoices.length === 0 ? <PortalEmptyState icon={Receipt} title="No invoices yet" desc="Create your first invoice to start tracking payments" action="Create Invoice" onAction={() => setShowForm(true)} /> : (
        <div className="space-y-2">
          {invoices.map(inv => (
            <div key={inv.invoice_id} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4 flex items-center gap-4" data-testid={`invoice-row-${inv.invoice_id}`}>
              <Receipt className="w-5 h-5 text-white/30 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><p className="text-white text-sm font-medium">{inv.invoice_number}</p><StatusBadge status={inv.status} /></div>
                <p className="text-white/40 text-xs">{inv.client_name} · {inv.property_address}</p>
                {inv.due_date && <p className="text-white/20 text-xs">Due: {new Date(inv.due_date).toLocaleDateString()}</p>}
              </div>
              <p className="text-[#D4AF37] font-bold">${inv.total?.toLocaleString()}</p>
              <div className="flex gap-1.5">
                {inv.status==="draft" && <button onClick={()=>updateStatus(inv.invoice_id,"sent")} className="text-xs px-2 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded"><Send className="w-3 h-3"/></button>}
                {inv.status==="sent" && <button onClick={()=>updateStatus(inv.invoice_id,"paid")} className="text-xs px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 text-[#00FF88] rounded"><CheckCircle className="w-3 h-3"/></button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
